"use client";

import React from "react";
import { ShieldCheck, Server, Layers, LockKeyhole, MapPin, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const services = [{
  icon: Layers,
  title: "External envelope access",
  description: "Full perimeter independent scaffolding, heavy-duty loading bays, and weatherproof temporary roofing for exterior cladding, glazing, and facade maintenance.",
  points: ["Perimeter scaffolding & hoist towers", "Weather-protected temporary roofs", "High-capacity material loading bays"]
}, {
  icon: Server,
  title: "Plant & MEP infrastructure decks",
  description: "Engineered support structures, internal birdcages, and high-load gantry systems designed to install and service chillers, generators, and AHU plant rooms.",
  points: ["Chiller & generator access gantries", "Internal crash decks & birdcages", "Pipe bridge and ductwork access"]
}, {
  icon: LockKeyhole,
  title: "Secure & live-facility protocols",
  description: "Vetted, CSCS/CISRS-qualified operatives trained to work inside high-security, live data environments with zero disruption to continuous operations.",
  points: ["Vetted teams & strict escort compliance", "Dust, debris & FOD containment measures", "24/7 emergency response and phased work"]
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "Home Counties"];
const accreditations = ["NASC", "CHAS", "Constructionline"];
export default function Datacentre() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: prefersReducedMotion ? {
      opacity: 1
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="datacentrescaffoldingservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header Block */}
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-medium text-xs bg-accent text-accent-foreground border-border">
            <ShieldCheck className="size-3.5 text-primary" />
            Mission-critical facility access
          </Badge>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Scaffolding for data centre construction, fit-out, and plant maintenance
          </h1>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd delivers engineered access solutions designed for high-specification data facilities. From external facade installations to live plant upgrades, we combine technical scaffolding design with strict site security protocols across the South East.
          </p>
        </div>

        {/* 3 Pillar Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-3 pb-2">
                    <div className="size-11 rounded-lg bg-accent text-primary flex items-center justify-center border border-border">
                      <IconComponent className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <Separator className="my-3" />
                    <ul className="space-y-2 text-sm text-card-foreground">
                      {item.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Coverage & Accreditations Banner */}
        <div className="mt-12 rounded-xl bg-secondary text-secondary-foreground p-6 sm:p-8 lg:p-10 shadow-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="flex items-center gap-2 text-secondary-foreground font-semibold text-sm">
                <MapPin className="size-4 text-primary" />
                <span>Regional operational coverage</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold font-default">
                Full scaffolding service coverage across the South East
              </h2>
              <p className="text-sm sm:text-base text-secondary-foreground/85 leading-relaxed">
                Operating with direct logistics and supervisor-led teams to ensure rapid turnaround, scheduled maintenance, and strict adherence to CDM regulations.
              </p>
              
              <div className="flex flex-wrap gap-2 pt-2">
                {coverageAreas.map((area, areaIdx) => <span key={area} data-index={areaIdx} className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-sidebar-accent text-sidebar-accent-foreground border border-sidebar-border">
                    {area}
                  </span>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col items-start lg:items-end justify-between gap-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-secondary-foreground/15">
              <div className="w-full lg:text-right space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground/75 block">
                  Audited & verified accreditations
                </span>
                <div className="flex flex-wrap lg:justify-end gap-2">
                  {accreditations.map((acc, accIdx) => <Badge key={acc} data-index={accIdx} variant="secondary" className="bg-card text-card-foreground border border-border text-xs px-2.5 py-1 font-semibold">
                      {acc}
                    </Badge>)}
                </div>
              </div>

              <div className="w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                    <span>Request a quote</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}